import { useCallback, useMemo, useRef, useState } from 'react';
import { X, Upload, Loader, Trash2, Check } from '../../icons';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { DocEditor } from '../scriptDoc/DocEditor';
import { draftStore } from '../scriptDoc/draftStore';
import { plainTextFromDoc } from '../scriptDoc/docText';
import { MAX_SCRIPT_CHARS, MAX_SCRIPT_DOC_BYTES } from '../scriptDoc/config';
import { isDismissablePopoverOpen } from '../../lib/useDismissOnOutsidePointer';
import type { ScriptDoc } from '@discovr/types';

type AddScriptModalProps = {
  open: boolean;
  onClose: () => void;
  creatorName: string;
  creatorId: string;
  rosterEntryId: string;
  onScriptSubmit: (
    creatorId: string,
    rosterEntryId: string,
    doc: ScriptDoc,
  ) => Promise<void>;
  isLoading?: boolean;
  /** How many comments are anchored to the CURRENT (about-to-be-replaced)
   *  script version — purely informational, shown as a plain count with no
   *  implication that it blocks anything (comments are reference tags;
   *  uploading always just archives the current version and starts fresh).
   *  Undefined while still loading; 0 or omitted renders nothing — "had 0
   *  comments" isn't worth saying. */
  previousCommentCount?: number;
};

const BYTES_PER_MEGABYTE = 1_000_000;
const DRAFT_RESTORED_LABEL = 'Draft restored';

const docByteLength = (doc: ScriptDoc): number => new TextEncoder().encode(JSON.stringify(doc)).length;

/** Mirrors the server's doc size cap so an oversized script fails fast with a
 *  clear message instead of a generic upload error. */
const isDocTooLarge = (doc: ScriptDoc): boolean => docByteLength(doc) > MAX_SCRIPT_DOC_BYTES;

const tooLargeMessage = (): string =>
  `Script is too large to upload (limit ${MAX_SCRIPT_DOC_BYTES / BYTES_PER_MEGABYTE} MB). Trim it and try again.`;

/** Indian digit grouping, so the limit reads as 1,00,000. */
const formatCount = (count: number): string => count.toLocaleString('en-IN');

const isScriptTooLong = (text: string): boolean => text.length > MAX_SCRIPT_CHARS;

const tooLongMessage = (): string =>
  `Script is too long (limit ${formatCount(MAX_SCRIPT_CHARS)} characters). Trim it and try again.`;

const savedAtLabel = (): string =>
  `Saved ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

/** Prevents Radix from moving focus to the first control on open so the editor
 *  (via its AutoFocusPlugin) receives it instead. */
const keepFocusForEditor = (event: Event) => event.preventDefault();

/** Escape closing a nested popover (the toolbar's colour palette) must not
 *  also close this dialog and silently discard the draft — both listen on
 *  `document`, so without this the dialog's own Escape-to-close fires
 *  regardless of the palette. */
const keepOpenWhilePopoverIsOpen = (event: Event) => {
  if (isDismissablePopoverOpen()) event.preventDefault();
};

/** The editor + footer. Lives inside DialogContent so it mounts fresh on each
 *  open — state (and the loaded draft) reset naturally without prev-open guards. */
function EditorPanel({
  creatorName,
  creatorId,
  rosterEntryId,
  onScriptSubmit,
  onClose,
  isLoading,
  previousCommentCount,
}: Omit<AddScriptModalProps, 'open'>) {
  // Load any saved draft exactly once, on mount.
  const initialDraft = useMemo(() => draftStore.load(rosterEntryId), [rosterEntryId]);
  const [doc, setDoc] = useState<ScriptDoc | null>(initialDraft);
  const [error, setError] = useState('');
  const [savedLabel, setSavedLabel] = useState<string | null>(initialDraft ? DRAFT_RESTORED_LABEL : null);
  const [hasDraft, setHasDraft] = useState(Boolean(initialDraft));
  // The editor emits once on mount to sync its normalised doc; that emission
  // must not clear a restored/saved label. Every later emission is a real
  // content edit (OnChangePlugin ignores selection-only changes), so it does.
  const mountEmissionSeen = useRef(false);

  // Recomputes only when the doc reference changes, not on every render —
  // plainTextFromDoc walks the whole tree, and OnChangePlugin emits a new
  // doc reference on every content-changing keystroke.
  const text = useMemo(() => plainTextFromDoc(doc), [doc]);
  const canUpload = text.trim().length > 0;

  const handleEditorChange = useCallback((next: ScriptDoc) => {
    setDoc(next);
    if (!mountEmissionSeen.current) {
      mountEmissionSeen.current = true;
      return;
    }
    // Only a shown label needs clearing; the functional form is a no-op
    // re-render when it is already null. A validation error about the
    // previous content is stale once the script changes, so it clears too.
    setSavedLabel((prev) => (prev === null ? prev : null));
    setError('');
  }, []);

  const handleSubmit = async () => {
    if (!doc || !canUpload) {
      setError('Please enter script content');
      return;
    }
    if (isScriptTooLong(text)) {
      setError(tooLongMessage());
      return;
    }
    if (isDocTooLarge(doc)) {
      setError(tooLargeMessage());
      return;
    }
    try {
      setError('');
      await onScriptSubmit(creatorId, rosterEntryId, doc);
      draftStore.clear(rosterEntryId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload script');
    }
  };

  const saveDraft = () => {
    if (!doc) return;
    draftStore.save(rosterEntryId, doc);
    setSavedLabel(savedAtLabel());
    setHasDraft(true);
  };

  const discardDraft = () => {
    draftStore.clear(rosterEntryId);
    setHasDraft(false);
    setSavedLabel(null);
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="min-w-0">
          <DialogTitle className="text-heading-h7 text-foreground">Add Script for {creatorName}</DialogTitle>
          <DialogDescription className="sr-only">
            Rich-text script editor. Use the toolbar above for formatting, or Cmd/Ctrl+B, I, U for bold, italic,
            underline. Save draft keeps your work without submitting.
          </DialogDescription>
          {/* Purely informational — a plain count, not a warning. Comments
              are reference tags on the script text; uploading a new version
              always just archives the current one and starts fresh,
              regardless of how many comments it had. */}
          {previousCommentCount ? (
            <p className="mt-1 text-caption-regular text-muted-foreground">
              Previous version had {previousCommentCount}{' '}
              {previousCommentCount === 1 ? 'comment' : 'comments'}.
            </p>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="icon"
          type="button"
          onClick={onClose}
          disabled={isLoading}
          aria-label="Close"
          className="size-9 shrink-0 rounded-lg border border-border text-muted-foreground hover:bg-accent"
        >
          <X size={18} />
        </Button>
      </div>

      {/* Editor */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <DocEditor initialDoc={initialDraft ?? undefined} creatorName={creatorName} onChange={handleEditorChange} />
      </div>

      {/* Footer */}
      <div className="border-t border-border bg-muted px-6 py-4">
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 text-caption-regular text-muted-foreground">
            <span className={isScriptTooLong(text) ? 'text-destructive' : undefined}>
              {formatCount(text.length)} / {formatCount(MAX_SCRIPT_CHARS)} characters
            </span>
            {savedLabel ? (
              <span className="inline-flex items-center gap-1 text-success">
                <Check size={12} />
                {savedLabel}
              </span>
            ) : null}
            {hasDraft ? (
              <button type="button" className="inline-flex items-center gap-1 underline" onClick={discardDraft}>
                <Trash2 size={12} />
                Discard draft
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="outline" type="button" onClick={saveDraft} disabled={isLoading || !canUpload}>
              Save draft
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={() => void handleSubmit()}
              disabled={isLoading || !canUpload}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader size={14} className="animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload size={14} />
                  Upload Script
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// Built on the shadcn Dialog so the background scroll-locks + inerts and focus
// is trapped while open. The editor body is a child of DialogContent so it
// remounts on each open — no manual state reset needed.
export function AddScriptModal({ open, onClose, isLoading = false, ...rest }: AddScriptModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isLoading) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        onOpenAutoFocus={keepFocusForEditor}
        onEscapeKeyDown={keepOpenWhilePopoverIsOpen}
        className="flex max-h-[92vh] w-[92vw] max-w-[720px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[720px]"
      >
        <EditorPanel {...rest} onClose={onClose} isLoading={isLoading} />
      </DialogContent>
    </Dialog>
  );
}
